import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  formatBp,
  addMoney,
  fechaEnNicaragua,
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  parseMoney,
  subMoney,
  type Centavos,
  type CuentaBancoCordobas,
  type Milli,
  type Settings,
} from '@abastece/shared';
import {
  MOTIVOS_LIQUIDACION,
  aniosDeServicio,
  lineaDeLiquidacion,
  lugarEnElLog,
  pagoDe,
  type Empleado,
  type MotivoLiquidacion,
  type PagoAguinaldo,
  type Planilla as Corrida,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas, deDondeSalio } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import {
  adelantosDados,
  aguinaldoAnulado,
  anulada,
  cada,
  costoDeCorrida,
  cotizacionesPorPagar,
  costoMensual,
  dichoAcordado,
  montoAcordado,
  muestraVacaciones,
  pagaPrestaciones,
  porDe,
  primerDiaDePago,
  quienesTienenAguinaldo,
  tituloDe,
} from '../lib/planilla.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { usePuede } from '../lib/sesion.js';
import { Campo, Selector } from '../ui/Campo.js';
import { Colillas } from '../ui/Colillas.js';
import { CorrerPlanilla } from '../ui/CorrerPlanilla.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { HojaEmpleado } from '../ui/HojaEmpleado.js';
import { PagarAguinaldo } from '../ui/PagarAguinaldo.js';
import { PagarCotizaciones } from '../ui/PagarCotizaciones.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { ConMarca, Explicacion, type PasoDelCalculo } from '../ui/Explicacion.js';
import { HojaVacaciones } from '../ui/HojaVacaciones.js';
import { SaleDeLaGaveta, faltaEnGaveta, useGaveta } from '../ui/SaleDeLaGaveta.js';
import { useT, type Clave } from '../i18n/index.js';

export function Planilla() {
  const t = useT();
  const puedeVer = usePuede('ver_reportes');
  const puedeGestionar = usePuede('gestionar_planilla');

  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const empleados = useLiveQuery(() => db.empleados.toArray(), [], []);
  const planillas = useLiveQuery(() => db.planillas.toArray(), [], []);
  const pagosCotizaciones = useLiveQuery(() => db.pagosCotizaciones.toArray(), [], []);
  const pagosAguinaldo = useLiveQuery(() => db.pagosAguinaldo.toArray(), [], []);

  const [vista, setVista] = useState<'gente' | 'corridas'>('gente');
  const [contratando, setContratando] = useState(false);
  const [editando, setEditando] = useState<Empleado | null>(null);
  const [corriendo, setCorriendo] = useState(false);
  const [liquidando, setLiquidando] = useState<Empleado | null>(null);
  const [adelantando, setAdelantando] = useState<Empleado | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  /** «Correr planilla» opened from «Registrar un pago anterior»: days already paid. */
  const [pagadaAlAbrir, setPagadaAlAbrir] = useState(false);
  /** «Pagar al INSS, al INATEC y a la DGI» open (2026-10-31). */
  const [pagandoCotizaciones, setPagandoCotizaciones] = useState(false);
  /** The run being set right (2026-10-31), read as «Corregir» opened it. */
  const [corrigiendo, setCorrigiendo] = useState<Corrida | null>(null);
  /** «Pagar aguinaldo» open (2026-10-31); or a payment of it being set right, read as «Corregir» opened it. */
  const [pagandoAguinaldo, setPagandoAguinaldo] = useState(false);
  const [corrigiendoAguinaldo, setCorrigiendoAguinaldo] = useState<PagoAguinaldo | null>(null);
  /** Whose pay stubs are open (2026-11-01): a run's or settlement's, or an aguinaldo payment's, read fresh from the books. */
  const [colillasDe, setColillasDe] = useState<{ tipo: 'corrida' | 'aguinaldo'; id: string } | null>(null);
  // When each run, or aguinaldo payment, was last set right: said on it.
  const corregidas = useLiveQuery(
    async () => {
      const [eventos, sinEfecto] = await Promise.all([db.eventos.where('type').anyOf(['planilla.corregida', 'aguinaldo.corregido']).toArray(), leerSinEfecto()]);
      const cuando = new Map<string, string>();
      for (const e of eventos.filter((x) => tuvoEfecto(x, sinEfecto)).sort((a, b) => lugarEnElLog(a) - lugarEnElLog(b))) {
        const p = e.payload as { planillaId?: string; pagoId?: string };
        cuando.set(p.planillaId ?? p.pagoId ?? '', fechaEnNicaragua(e.clientTs));
      }
      return cuando;
    },
    [],
    new Map<string, string>(),
  );
  /** Whose vacation days are being written down (2026-10-31), read fresh from the books. */
  const [descansandoId, setDescansandoId] = useState<string | null>(null);
  const descansando = descansandoId === null ? null : (empleados.find((e) => e.id === descansandoId) ?? null);

  // --- pago de una corrida o una liquidación ---
  /** The run or settlement being paid, read fresh from the books: one just recorded may not be there yet. */
  const [pagandoId, setPagandoId] = useState<string | null>(null);
  const pagando: Corrida | null = pagandoId === null ? null : (planillas.find((p) => p.id === pagandoId) ?? null);
  const [formaPago, setFormaPago] = useState<'efectivo_cordobas' | 'transferencia' | 'billetera'>('efectivo_cordobas');
  /** The córdoba account a payment by transfer goes out of, once picked. */
  const [cuentaPago, setCuentaPago] = useState<CuentaBancoCordobas | null>(null);
  /** The day it was paid: today, or a day since the run (2026-10-31: «log payments made in past pay periods»). */
  const [fechaPago, setFechaPago] = useState(hoy());
  /**
   * The first day it can say it was paid: its own, or that of an advance it took off, given before it
   * was written down (`primerDiaDePago`). Dated before, its payment booked less than was handed over.
   */
  const primerDia = useLiveQuery(
    async () => {
      if (!pagando) return null;
      const [eventos, sinEfecto] = await Promise.all([
        db.eventos.where('type').anyOf(['empleado.adelanto', 'planilla.corrida', 'planilla.corregida', 'empleado.liquidado']).toArray(),
        leerSinEfecto(),
      ]);
      const tuvo = (e: (typeof eventos)[number]) => tuvoEfecto(e, sinEfecto);
      // Written down, or last set right (2026-10-31): a correction may take off advances given since.
      const suyas = eventos.filter((e) => {
        const p = e.payload as { planillaId?: string; empleadoId?: string };
        return (
          tuvo(e) &&
          (e.type === 'planilla.corrida' || e.type === 'planilla.corregida' ? p.planillaId === pagando.id : e.type === 'empleado.liquidado' && `liq:${p.empleadoId}` === pagando.id)
        );
      });
      return primerDiaDePago(pagando, adelantosDados(eventos, tuvo), suyas.length > 0 ? Math.max(...suyas.map(lugarEnElLog)) : Number.POSITIVE_INFINITY);
    },
    [pagando],
    null,
  );
  const desdeElDia = primerDia?.fecha ?? pagando?.fecha ?? '';
  const fechaPagoBien = pagando !== null && /^\d{4}-\d{2}-\d{2}$/.test(fechaPago) && fechaPago >= desdeElDia && fechaPago <= hoy();

  function abrirPago(planillaId: string) {
    setPagandoId(planillaId);
    setFechaPago(hoy());
    setFormaPago('efectivo_cordobas');
    setCuentaPago(null);
    setError(null);
  }

  // --- liquidación ---
  const [motivo, setMotivo] = useState<MotivoLiquidacion>('renuncia');
  const [fechaSalida, setFechaSalida] = useState(hoy());
  const [salarioPendiente, setSalarioPendiente] = useState('');

  // --- adelanto ---
  const [montoAdelanto, setMontoAdelanto] = useState('');
  const [formaAdelanto, setFormaAdelanto] = useState<'efectivo_cordobas' | 'transferencia' | 'billetera'>('efectivo_cordobas');
  const [notaAdelanto, setNotaAdelanto] = useState('');
  // Off until somebody turns it on, as on a purchase and an expense (the owner, 2026-10-17).
  const gaveta = useGaveta(formaAdelanto, { encendidaAlEmpezar: false });
  /** The córdoba account an advance by transfer goes out of, once picked. */
  const [cuentaAdelanto, setCuentaAdelanto] = useState<CuentaBancoCordobas | null>(null);
  const faltaAdelanto = faltaEnGaveta(gaveta, parseMoney(montoAdelanto) ?? 0, t);

  // What the runs withheld and owe the INSS, the INATEC and the DGI, the books' figures, and its last payment.
  const cotizaciones = cotizacionesPorPagar(planillas, pagosCotizaciones);
  const ultimoPagoCotizaciones = [...pagosCotizaciones].sort((a, b) => b.fecha.localeCompare(a.fecha))[0];

  const activos = empleados.filter((e) => e.activo).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  const salidos = empleados.filter((e) => !e.activo);
  const corridas = [...planillas].sort((a, b) => b.fecha.localeCompare(a.fecha));
  /** «Corridas», newest first: the runs and settlements, and the aguinaldo paid on its own (2026-10-31). */
  const entradas = [
    ...corridas.map((p) => ({ tipo: 'corrida' as const, fecha: p.fecha, p })),
    ...pagosAguinaldo.map((a) => ({ tipo: 'aguinaldo' as const, fecha: a.fecha, a })),
  ].sort((x, y) => y.fecha.localeCompare(x.fecha));

  const provisiones = activos.reduce(
    (a, e) => ({
      aguinaldo: addMoney(a.aguinaldo, e.aguinaldoAcumulado),
      vacaciones: addMoney(a.vacaciones, e.vacacionesAcumuladas),
    }),
    { aguinaldo: 0 as Centavos, vacaciones: 0 as Centavos },
  );

  /**
   * What the payroll really costs, not what it pays.
   *
   * Salary plus 23.5% employer contributions plus the two monthly provisions — a sixth of a
   * salary between them. An owner who budgets on the payslip figure is short by a third. Off INSS
   * or off the benefits, without them; somebody paid by the day costs what they work, which is
   * not known before, and is left out with a word saying so.
   */
  const costo = costoMensual(activos, config);

  /*
   * What each figure at the top is made of (the owner, 2026-10-31: «can we have tooltips added to the
   * data card at the top of the payroll page? like what goes into the "Monthly cost" calculation»),
   * with the figures it adds up, as «Contabilidad» does.
   */
  const pasos = (lineas: readonly { etiqueta: string; monto: number }[], total: string): PasoDelCalculo[] => [
    ...lineas.map((l, i) => ({ ...(i > 0 ? { signo: '+' as const } : {}), etiqueta: l.etiqueta, monto: formatMoney(l.monto as Centavos) })),
    { signo: '=' as const, etiqueta: total, monto: formatMoney(lineas.reduce((a, l) => a + l.monto, 0) as Centavos) },
  ];
  const calculoCosto = pasos(
    [
      { etiqueta: t('planilla.calc.salarios'), monto: costo.partes.salarios },
      ...(costo.partes.inssPatronal > 0 ? [{ etiqueta: t('planilla.calc.inssPatronal', { tasa: formatBp(config.inssPatronalBp) }), monto: costo.partes.inssPatronal }] : []),
      ...(costo.partes.inatec > 0 ? [{ etiqueta: t('planilla.calc.inatec', { tasa: formatBp(config.inatecBp) }), monto: costo.partes.inatec }] : []),
      { etiqueta: t('planilla.calc.aguinaldo'), monto: costo.partes.aguinaldo },
      ...(config.vacacionesAcumulan || costo.partes.vacaciones > 0 ? [{ etiqueta: t('planilla.calc.vacaciones'), monto: costo.partes.vacaciones }] : []),
    ],
    t('planilla.costoMensual'),
  );
  // Vacations off for the shop (2026-10-31): their figure goes too, once nobody has any set aside.
  const verVacaciones = config.vacacionesAcumulan || provisiones.vacaciones > 0 || activos.some((e) => e.diasVacacionesAcumulados > 0);
  const deCadaQuien = (monto: (e: Empleado) => number, etiqueta: (e: Empleado) => string, total: string) => {
    const lineas = activos.filter((e) => monto(e) !== 0).map((e) => ({ etiqueta: etiqueta(e), monto: monto(e) }));
    return lineas.length > 0 ? pasos(lineas, total) : undefined;
  };
  const sinPagarAun = corridas.filter((p) => !p.pagada && p.totalNeto - (p.totalAdelantos ?? 0) !== 0);

  async function adelantar() {
    const monto = parseMoney(montoAdelanto);
    if (!adelantando || monto === null || monto <= 0 || faltaAdelanto) return;
    setError(null);
    setTrabajando(true);
    try {
      const rechazo = await emitirYRevisar('empleado.adelanto', {
        adelantoId: newId(),
        empleadoId: adelantando.id,
        fecha: hoy(),
        monto,
        formaPago: formaAdelanto,
        ...(formaAdelanto === 'transferencia' ? { cuenta: cuentaEnCordobas(config, cuentaAdelanto) } : {}),
        ...(gaveta.sesionId ? { sesionId: gaveta.sesionId } : {}),
        ...(notaAdelanto.trim() ? { nota: notaAdelanto.trim() } : {}),
      });
      if (rechazo) setError(rechazo);
      else setAdelantando(null);
    } catch (e) {
      // Not recorded at all: said in the sheet, which stays open.
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /** Emits and surfaces a reducer rejection instead of failing silently. */
  async function emitirYRevisar(tipo: 'empleado.adelanto', payload: unknown): Promise<string | null> {
    return (await emitirYVer(tipo, payload as never)).rechazo;
  }

  async function liquidar() {
    if (!liquidando) return;
    // Typed and unreadable is not nothing pending: said, not paid as zero.
    const pendiente = salarioPendiente.trim() === '' ? 0 : parseMoney(salarioPendiente);
    if (pendiente === null) {
      setError(t('planilla.montoInvalido'));
      return;
    }
    setTrabajando(true);
    setError(null);
    try {
      // Refused, the sheet stays open and says why: it used to close as if the settlement were done.
      const { rechazo } = await emitirYVer('empleado.liquidado', {
        empleadoId: liquidando.id,
        fecha: fechaSalida,
        motivo,
        salarioPendiente: pendiente,
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setLiquidando(null);
      setSalarioPendiente('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * A run or a settlement paid, the way it was handed over: out of general cash, a bank account or
   * the wallet. One tap used to book it out of general cash whatever it was paid with, and a payment
   * has no undoing (2026-10-31). Refused, the sheet says why and stays open.
   */
  async function pagar() {
    if (!pagando || !fechaPagoBien) return;
    setError(null);
    setTrabajando(true);
    try {
      const { rechazo } = await emitirYVer('planilla.pagada', {
        planillaId: pagando.id,
        fecha: fechaPago,
        formaPago,
        ...(formaPago === 'transferencia' ? { cuenta: cuentaEnCordobas(config, cuentaPago) } : {}),
      });
      if (rechazo) setError(rechazo);
      else setPagandoId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  // What the settlement pays, as the books will book it (`lineaDeLiquidacion`): the law's amounts,
  // what comes off them, and what is handed over. Nothing before there is a last day: a date box
  // emptied stopped the whole page.
  const liquidacion =
    liquidando && /^\d{4}-\d{2}-\d{2}$/.test(fechaSalida)
      ? lineaDeLiquidacion(
          {
            empleado: liquidando,
            fecha: fechaSalida,
            motivo,
            salarioPendiente: (parseMoney(salarioPendiente) ?? 0) as Centavos,
            corridas: planillas,
          },
          config,
        )
      : null;
  const lineaLiquidacion = liquidacion?.calculada.linea ?? null;

  if (!puedeVer) {
    return (
      <Pagina titulo={t('planilla.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('planilla.titulo')}
      subtitulo={t('planilla.personas', { n: activos.length })}
      ancho="amplio"
      acciones={
        puedeGestionar && (
          <button
            type="button"
            className="btn-primario gap-1.5 px-4 text-sm"
            data-guia="planilla-correr"
            onClick={() => {
              setPagadaAlAbrir(false);
              setCorriendo(true);
            }}
          >
            <Ico icono="calcular" className="size-4" />
            {t('planilla.correr')}
          </button>
        )
      }
    >
      <section className={`tarjeta mb-4 grid grid-cols-2 gap-4 ${verVacaciones ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}>
        <Cifra
          titulo={t('planilla.costoMensual')}
          valor={costo.costo}
          pie={costo.porDia > 0 ? t('planilla.pieCostoSinDia', { n: costo.porDia }) : t('planilla.pieCosto')}
          ayuda={t(config.vacacionesAcumulan ? 'planilla.ayuda.costoMensual' : 'planilla.ayuda.costoMensualSinVacaciones', {
            inss: formatBp(config.inssPatronalBp),
            inatec: formatBp(config.inatecBp),
          })}
          calculo={calculoCosto}
        />
        <Cifra
          titulo={t('planilla.aguinaldoAcumulado')}
          valor={provisiones.aguinaldo}
          pie={t('planilla.loQueSeDebe')}
          ayuda={t('planilla.ayuda.aguinaldo')}
          calculo={deCadaQuien((e) => e.aguinaldoAcumulado, (e) => e.nombre, t('planilla.aguinaldoAcumulado'))}
        />
        {verVacaciones && (
          <Cifra
            titulo={t('planilla.vacacionesAcumuladas')}
            valor={provisiones.vacaciones}
            pie={t('planilla.loQueSeDebe')}
            ayuda={t('planilla.ayuda.vacaciones')}
            calculo={deCadaQuien(
              (e) => e.vacacionesAcumuladas,
              (e) => t('planilla.calc.diasDe', { nombre: e.nombre, dias: formatQuantity(e.diasVacacionesAcumulados as Milli) }),
              t('planilla.vacacionesAcumuladas'),
            )}
          />
        )}
        <Cifra
          titulo={t('planilla.sinPagar')}
          valor={corridas.filter((p) => !p.pagada).reduce((a, p) => a + p.totalNeto - (p.totalAdelantos ?? 0), 0) as Centavos}
          ayuda={t('planilla.ayuda.sinPagar')}
          {...(sinPagarAun.length > 0
            ? {
                calculo: pasos(
                  sinPagarAun.map((p) => ({
                    etiqueta: p.esLiquidacion ? t('planilla.liquidacionDe', { nombre: p.lineas[0]?.nombre ?? '' }) : tituloDe(p),
                    monto: p.totalNeto - (p.totalAdelantos ?? 0),
                  })),
                  t('planilla.sinPagar'),
                ),
              }
            : {})}
        />
        {/* The aguinaldo, paid on its own (the owner, 2026-10-31): a button of its own, not a box on every run's line. */}
        {puedeGestionar && quienesTienenAguinaldo(activos).length > 0 && (
          <div className="col-span-full flex flex-col gap-3 border-t border-white/5 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-400">{t('planilla.aguinaldo.cuando')}</p>
            <button
              type="button"
              className="btn-secundario h-11 min-h-0 shrink-0 gap-1.5 px-4 text-sm"
              data-pagar-aguinaldo
              onClick={() => setPagandoAguinaldo(true)}
            >
              <Ico icono="aguinaldo" className="size-4" />
              {t('planilla.aguinaldo.pagar')}
            </button>
          </div>
        )}
      </section>

      <div className="mb-4 flex gap-2 border-b border-slate-800">
        {(['gente', 'corridas'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVista(v)}
            data-sin-icono="pestana"
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
              vista === v
                ? 'border-marca-500 font-medium text-slate-100'
                : 'border-transparent text-slate-500 hover:border-slate-500 hover:text-slate-300'
            }`}
          >
            {t(v === 'gente' ? 'planilla.personal' : 'planilla.corridas')}
          </button>
        ))}
      </div>

      {vista === 'gente' && (
        <>
          {puedeGestionar && (
            <button
              type="button"
              className="btn-secundario mb-4 w-full text-sm"
              onClick={() => setContratando(true)}
            >
              <Ico icono="contratar" />
              {t('planilla.contratar')}
            </button>
          )}

          {activos.length === 0 ? (
            <Vacio mensaje={t('planilla.nadie')} icono="personal" />
          ) : (
            <ul className="flex flex-col gap-2" data-guia="planilla-gente">
              {activos.map((e) => {
                const pago = pagoDe(e);
                return (
                <li key={e.id} className="tarjeta flex flex-col gap-3" data-empleado={e.nombre}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{e.nombre}</p>
                      {/* With the buttons under the card, the job and the years have the room to wrap instead of being cut. */}
                      <p className="mt-0.5 text-xs text-slate-500">
                        {t('planilla.desdeAnios', {
                          cargo: e.cargo,
                          fecha: e.fechaIngreso,
                          anios: aniosDeServicio(e.fechaIngreso, hoy()).toFixed(1),
                        })}
                      </p>
                      {/* How they are paid, where it is not a fixed pay with everything: the amount says the rest. Paid by the day, when. */}
                      {(pago.porDia || !pago.conInss || !pago.conPrestaciones) && (
                        <p className="mt-1 flex flex-wrap gap-1 text-xs" data-como-se-paga>
                          {pago.porDia && (
                            <span className="rounded-full bg-slate-700/60 px-2 py-0.5 text-slate-300">
                              {t(`planilla.frecuencia.${pago.frecuencia}`)}
                            </span>
                          )}
                          {!pago.conInss && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-300">{t('planilla.sinInss')}</span>
                          )}
                          {!pago.conPrestaciones && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-300">{t('planilla.sinPrestaciones')}</span>
                          )}
                        </p>
                      )}
                      {pagaPrestaciones(e) && (
                        <p data-guia="planilla-acumulado" className="mt-0.5 text-xs text-slate-500">
                          {muestraVacaciones(e, config)
                            ? t('planilla.acumulado', {
                                aguinaldo: formatMoney(e.aguinaldoAcumulado),
                                vacaciones: formatMoney(e.vacacionesAcumuladas),
                                dias: (e.diasVacacionesAcumulados / 1000).toFixed(1),
                              })
                            : t('planilla.acumuladoAguinaldo', { aguinaldo: formatMoney(e.aguinaldoAcumulado) })}
                        </p>
                      )}
                      {(e.adelantosPendientes ?? 0) > 0 && (
                        <p className="mt-0.5 text-xs text-amber-300">
                          {t('planilla.debeAdelantos', { monto: formatMoney(e.adelantosPendientes!) })}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-right">
                      <span className="tabular block font-semibold" data-acordado>{formatMoney(montoAcordado(e))}</span>
                      <span className="block text-xs text-slate-500">{cada(porDe(e))}</span>
                      {/* A fixed pay every week or fifteen days, and what it comes to a month: the salary. */}
                      {porDe(e) === 'semanal' || porDe(e) === 'quincenal' ? (
                        <span className="tabular block text-xs text-slate-500" data-al-mes>
                          {dichoAcordado(e.salarioMensual, 'mensual')}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {/*
                   * What can be done with them, as buttons along the bottom of the card, two to a row on a
                   * phone and four on a wider screen (the owner, 2026-10-31: «the little links for "edit,
                   * advance, holidays, settleup" on the card are so hard to see»): they were 12px underlined
                   * words stacked under the pay. Three of them, the last takes the whole row. Each word on one
                   * line: at 320 px «Settle up» broke in two inside its button, and «Vacaciones» was cut, so under
                   * 360 px the words are a size smaller.
                   */}
                  {puedeGestionar && (
                    <div className="grid grid-cols-2 gap-2 border-t border-white/5 pt-3 sm:grid-cols-4" data-acciones-empleado>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm max-[359px]:text-xs"
                        onClick={() => setEditando(e)}
                      >
                        <Ico icono="editar" className="size-4" />
                        <span className="truncate">{t('planilla.editar')}</span>
                      </button>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm max-[359px]:text-xs"
                        onClick={() => {
                          setAdelantando(e);
                          setMontoAdelanto('');
                          setNotaAdelanto('');
                          setFormaAdelanto('efectivo_cordobas');
                          gaveta.setEncendido(false);
                          setError(null);
                        }}
                      >
                        <Ico icono="pagar" className="size-4" />
                        <span className="truncate">{t('planilla.adelanto')}</span>
                      </button>
                      {/* Days rested, written down when they happen (2026-10-31): their run pays them, out of what was set aside. */}
                      {!pago.porDia && muestraVacaciones(e, config) && (
                        <button
                          type="button"
                          className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm max-[359px]:text-xs"
                          data-vacaciones
                          onClick={() => setDescansandoId(e.id)}
                        >
                          <Ico icono="vacaciones" className="size-4" />
                          <span className="truncate">{t('planilla.vacaciones')}</span>
                        </button>
                      )}
                      {/* Ending the contract: rose, as what undoes is everywhere. */}
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm max-[359px]:text-xs text-rose-300 odd:last:col-span-2"
                        onClick={() => {
                          setLiquidando(e);
                          setFechaSalida(hoy());
                          setMotivo('renuncia');
                          // Each settlement its own: what was typed for somebody else used to stay.
                          setSalarioPendiente('');
                          setError(null);
                        }}
                      >
                        <Ico icono="liquidar" className="size-4" />
                        <span className="truncate">{t('planilla.liquidar')}</span>
                      </button>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}

          {salidos.length > 0 && (
            <section className="tarjeta mt-4">
              <h2 className="mb-2 text-sm font-semibold text-slate-400">{t('planilla.yaNoEstan')}</h2>
              <ul className="flex flex-col gap-1 text-sm">
                {salidos.map((e) => (
                  <li key={e.id} className="flex justify-between gap-3">
                    <span className="truncate text-slate-400">{e.nombre}</span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {e.fechaSalida} ·{' '}
                      {e.motivoSalida ? t(`salida.${e.motivoSalida}` as Clave) : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {vista === 'corridas' && (
        <>
          {/* Days already paid, before the app or since (the owner, 2026-10-31): the run and its payment in one. */}
          {puedeGestionar && (
            <button
              type="button"
              className="btn-secundario mb-4 w-full text-sm"
              data-pago-anterior
              onClick={() => {
                setPagadaAlAbrir(true);
                setCorriendo(true);
              }}
            >
              <Ico icono="calendario" />
              {t('planilla.pagoAnterior')}
            </button>
          )}
          {(cotizaciones.inss !== 0 || cotizaciones.inatec !== 0 || cotizaciones.ir !== 0 || ultimoPagoCotizaciones) && (
            <section className="tarjeta mb-4" data-cotizaciones-por-pagar>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold">{t('planilla.cotizacionesPorPagar')}</h2>
                  <p className="tabular mt-0.5 text-xs text-slate-400" data-cotizaciones-debe>
                    {t('planilla.cotizaciones', {
                      inss: formatMoney(cotizaciones.inss),
                      inatec: formatMoney(cotizaciones.inatec),
                      ir: formatMoney(cotizaciones.ir),
                    })}
                  </p>
                  {ultimoPagoCotizaciones && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {t('planilla.ultimoPagoCotizaciones', { periodo: ultimoPagoCotizaciones.periodo, fecha: ultimoPagoCotizaciones.fecha })}
                    </p>
                  )}
                </div>
                {puedeGestionar && (
                  <button type="button" className="btn-secundario shrink-0 gap-1.5 text-sm" data-abrir-cotizaciones onClick={() => setPagandoCotizaciones(true)}>
                    <Ico icono="pagar" className="size-4" />
                    {t('planilla.pagarCotizacionesCorto')}
                  </button>
                )}
              </div>
            </section>
          )}
          {entradas.length === 0 ? (
            <Vacio mensaje={t('planilla.sinCorridas')} />
          ) : (
            <ul className="flex flex-col gap-2">
              {entradas.map((x) => {
                // The aguinaldo paid on its own (2026-10-31), among the runs by its day.
                if (x.tipo === 'aguinaldo') {
                  return (
                    <TarjetaAguinaldo
                      key={x.a.id}
                      pago={x.a}
                      config={config}
                      corregidoEl={corregidas.get(x.a.id)}
                      onCorregir={puedeGestionar ? () => setCorrigiendoAguinaldo(x.a) : undefined}
                      onColillas={() => setColillasDe({ tipo: 'aguinaldo', id: x.a.id })}
                    />
                  );
                }
                const p = x.p;
                return (
                  <li key={p.id} className="tarjeta" data-corrida={p.id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">
                          {p.esLiquidacion ? t('planilla.liquidacionDe', { nombre: p.lineas[0]?.nombre ?? '' }) : tituloDe(p)}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {anulada(p)
                            ? p.fecha
                            : t('planilla.resumenCorrida', {
                                fecha: p.fecha,
                                n: p.lineas.length,
                                costo: formatMoney(costoDeCorrida(p)),
                              })}
                        </p>
                        {corregidas.has(p.id) && (
                          <p className="mt-0.5 text-xs text-sky-300" data-corregida>
                            {t('planilla.corregidaEl', { fecha: corregidas.get(p.id)! })}
                          </p>
                        )}
                      </div>
                      {/* Voided by a correction: nobody on it, nothing to pay. */}
                      {anulada(p) ? (
                        <span className="shrink-0 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-300" data-anulada>
                          {t('planilla.anulada')}
                        </span>
                      ) : (
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="tabular font-semibold">{formatMoney(p.totalNeto)}</span>
                          {(p.totalAdelantos ?? 0) > 0 && (
                            <span className="tabular text-xs text-slate-400">
                              {t('planilla.entregar', { monto: formatMoney(subMoney(p.totalNeto, p.totalAdelantos!)) })}
                            </span>
                          )}
                          {p.pagada ? (
                            <span className="text-xs text-teal-400">
                              {t('planilla.pagadaEl', { fecha: p.pagadaEn ?? '' })}
                            </span>
                          ) : (
                            puedeGestionar && (
                              <button
                                type="button"
                                className="btn-texto gap-1 text-xs text-amber-300"
                                onClick={() => abrirPago(p.id)}
                              >
                                <Ico icono="pagar" className="size-3.5" />
                                {t('planilla.marcarPagada')}
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    {!anulada(p) && (
                      <>
                        <ul className="mt-2 flex flex-col gap-0.5 text-xs text-slate-400">
                          {p.lineas.map((l) => (
                            // What it comes to wraps under the name on a narrow phone: with an advance taken off, it ran out of the card.
                            <li key={l.empleadoId} className="flex flex-wrap justify-between gap-x-3">
                              <span className="min-w-0 truncate">
                                {l.nombre}
                                {l.dias !== undefined && ` · ${t('planilla.diasN', { n: formatQuantity(l.dias as Milli) })}`}
                              </span>
                              <span className="tabular ml-auto text-right">
                                {formatMoney(l.brutoTotal, { symbol: false })} −{' '}
                                {formatMoney(addMoney(l.inssLaboral, l.irRetenido), { symbol: false })} ={' '}
                                {formatMoney(l.neto, { symbol: false })}
                                {(l.adelantoDescontado ?? 0) > 0 && (
                                  <>
                                    {' · '}
                                    <span className="whitespace-nowrap">
                                      {t('planilla.menosAdelanto', { monto: formatMoney(l.adelantoDescontado!, { symbol: false }) })}
                                    </span>
                                  </>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-xs text-slate-600">
                          {t('planilla.cotizaciones', {
                            inss: formatMoney(addMoney(p.totalInssLaboral, p.totalInssPatronal)),
                            inatec: formatMoney(p.totalInatec),
                            ir: formatMoney(p.totalIr),
                          })}
                        </p>
                      </>
                    )}
                    {/*
                     * Under it: set right after it was entered (the owner, 2026-10-31), its lines, whom it pays, or
                     * voided; and each person's pay stub (2026-11-01), to print or send. Side by side; alone, one
                     * takes the row.
                     */}
                    {!anulada(p) && (
                      <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/5 pt-3">
                        {puedeGestionar && !p.esLiquidacion && (
                          <button
                            type="button"
                            className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm odd:last:col-span-2"
                            data-corregir-corrida
                            onClick={() => {
                              setCorrigiendo(p);
                              setError(null);
                            }}
                          >
                            <Ico icono="editar" className="size-4" />
                            <span className="truncate">{t('planilla.corregir.boton')}</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm odd:last:col-span-2"
                          data-colillas
                          onClick={() => setColillasDe({ tipo: 'corrida', id: p.id })}
                        >
                          <Ico icono="imprimir" className="size-4" />
                          <span className="truncate">{t(p.lineas.length === 1 ? 'planilla.colillas.botonUna' : 'planilla.colillas.boton')}</span>
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {descansando && <HojaVacaciones key={descansando.id} empleado={descansando} onCerrar={() => setDescansandoId(null)} />}

      {pagandoCotizaciones && (
        <PagarCotizaciones corridas={planillas} pagos={pagosCotizaciones} config={config} onCerrar={() => setPagandoCotizaciones(false)} />
      )}

      {/* A sheet over the people, not a form above them (the owner, 2026-10-06). */}
      {contratando && <HojaEmpleado config={config} onCerrar={() => setContratando(false)} />}
      {editando && <HojaEmpleado key={editando.id} empleado={editando} config={config} onCerrar={() => setEditando(null)} />}

      {corriendo && (
        <CorrerPlanilla
          activos={activos}
          corridas={corridas}
          config={config}
          pagadaAlAbrir={pagadaAlAbrir}
          onCerrar={(pagoPendiente) => {
            setCorriendo(false);
            // Recorded, its payment refused: the run waits to be paid, its sheet open and saying why.
            if (pagoPendiente) {
              setVista('corridas');
              abrirPago(pagoPendiente.planillaId);
              setError(pagoPendiente.motivo);
            }
          }}
        />
      )}

      {(pagandoAguinaldo || corrigiendoAguinaldo) && (
        <PagarAguinaldo
          key={corrigiendoAguinaldo?.id ?? 'nuevo'}
          activos={activos}
          empleados={empleados}
          config={config}
          {...(corrigiendoAguinaldo ? { corrigiendo: corrigiendoAguinaldo } : {})}
          onCerrar={() => {
            setPagandoAguinaldo(false);
            setCorrigiendoAguinaldo(null);
          }}
        />
      )}

      {corrigiendo && (
        <CorrerPlanilla
          key={corrigiendo.id}
          activos={activos}
          empleados={empleados}
          corridas={corridas}
          config={config}
          corrigiendo={corrigiendo}
          onCerrar={() => setCorrigiendo(null)}
        />
      )}

      {colillasDe && (
        <Colillas
          key={colillasDe.id}
          {...(colillasDe.tipo === 'corrida'
            ? { corrida: planillas.find((p) => p.id === colillasDe.id) }
            : { aguinaldo: pagosAguinaldo.find((a) => a.id === colillasDe.id) })}
          empleados={empleados}
          config={config}
          onCerrar={() => setColillasDe(null)}
        />
      )}

      {liquidando && (
        <Modal
          titulo={t('planilla.liquidarA', { nombre: liquidando.nombre })}
          onCerrar={() => setLiquidando(null)}
        >
          <div className="flex flex-col gap-4">
            <Campo
              etiqueta={t('planilla.ultimoDia')}
              type="date"
              value={fechaSalida}
              min={liquidando.fechaIngreso}
              onChange={(e) => setFechaSalida(e.target.value)}
            />
            <Selector
              etiqueta={t('comun.motivo')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value as MotivoLiquidacion)}
            >
              {MOTIVOS_LIQUIDACION.map((k) => (
                <option key={k} value={k}>
                  {t(`salida.${k}` as Clave)}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta={t('planilla.salarioPendiente')}
              inputMode="decimal"
              className="tabular"
              value={salarioPendiente}
              onChange={(e) => setSalarioPendiente(e.target.value)}
              placeholder="0.00"
              ayuda={t('planilla.ayudaPendiente')}
            />

            {liquidacion && lineaLiquidacion && (
              <div className="rounded-xl bg-slate-950/40 ring-1 ring-white/5 ring-inset p-3 text-sm" data-liquidacion>
                <p className="mb-2 text-xs text-slate-400">{t('planilla.seLeVaAPagar')}</p>
                {pagoDe(liquidando).conPrestaciones && (
                  <Renglon
                    etiqueta={t('planilla.indemnizacion', { anios: liquidacion.liquidacion.anios.toFixed(2) })}
                    monto={lineaLiquidacion.indemnizacion}
                  />
                )}
                {/* Up to the last day, at the salary the contract ends on: as the books pay it. */}
                <Renglon etiqueta={t('planilla.aguinaldoAcumulado')} monto={lineaLiquidacion.aguinaldoPagado} />
                {(config.vacacionesAcumulan || lineaLiquidacion.vacacionesPagadas > 0) && (
                  <Renglon etiqueta={t('planilla.vacacionesAcumuladas')} monto={lineaLiquidacion.vacacionesPagadas} />
                )}
                <Renglon etiqueta={t('planilla.salarioPendienteCorto')} monto={lineaLiquidacion.bruto} />
                {/* What comes off, as the books take it: their INSS and income tax on the vacation and the salary, and the advances, never past the rest. */}
                {lineaLiquidacion.inssLaboral > 0 && (
                  <Renglon etiqueta={t('planilla.menosInss')} monto={subMoney(0 as Centavos, lineaLiquidacion.inssLaboral)} />
                )}
                {lineaLiquidacion.irRetenido > 0 && (
                  <Renglon etiqueta={t('planilla.menosIr')} monto={subMoney(0 as Centavos, lineaLiquidacion.irRetenido)} />
                )}
                {(lineaLiquidacion.adelantoDescontado ?? 0) > 0 && (
                  <Renglon etiqueta={t('planilla.menosAdelantos')} monto={subMoney(0 as Centavos, lineaLiquidacion.adelantoDescontado!)} />
                )}
                <div className="mt-1 flex justify-between gap-3 border-t border-white/10 pt-1.5 font-semibold" data-a-entregar>
                  <span>{t('planilla.aEntregar')}</span>
                  <span className="tabular">{formatMoney(subMoney(lineaLiquidacion.neto, lineaLiquidacion.adelantoDescontado ?? (0 as Centavos)))}</span>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500">
              {t(pagoDe(liquidando).conPrestaciones ? 'planilla.explicacionLiquidacion' : 'planilla.explicacionSinPrestaciones')}
            </p>

            {error && <p className="text-sm text-rose-400">{error}</p>}
            <button
              type="button"
              className="btn-primario"
              disabled={trabajando || liquidacion === null}
              onClick={() => void liquidar()}
            >
              <Ico icono="liquidar" />
              {t(trabajando ? 'planilla.liquidando' : 'planilla.liquidarYCerrar')}
            </button>
          </div>
        </Modal>
      )}

      {adelantando && (
        <Modal titulo={t('planilla.adelantoA', { nombre: adelantando.nombre })} onCerrar={() => setAdelantando(null)}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-400">{t('planilla.adelantoAyuda', { nombre: adelantando.nombre })}</p>
            <Campo
              etiqueta={t('planilla.montoAdelanto')}
              inputMode="decimal"
              className="tabular"
              value={montoAdelanto}
              onChange={(e) => setMontoAdelanto(e.target.value)}
              placeholder="0.00"
              error={faltaAdelanto}
              autoFocus
            />
            <Selector
              etiqueta={t('compra.formaPago')}
              value={formaAdelanto}
              onChange={(e) => setFormaAdelanto(e.target.value as typeof formaAdelanto)}
            >
              <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
              <option value="transferencia">{t('pago.transferencia')}</option>
              <option value="billetera">{t('gastos.billeteraMovil')}</option>
            </Selector>
            {formaAdelanto === 'transferencia' && (
              <ElegirCuenta
                valor={cuentaEnCordobas(config, cuentaAdelanto)}
                onCambiar={(c) => setCuentaAdelanto(c as CuentaBancoCordobas)}
                etiqueta={t('bancos.pagaDe')}
              />
            )}
            <SaleDeLaGaveta gaveta={gaveta} />
            <Campo etiqueta={t('comun.nota')} value={notaAdelanto} onChange={(e) => setNotaAdelanto(e.target.value)} maxLength={300} />
            {error && <p className="text-sm text-rose-400">{error}</p>}
            <button
              type="button"
              className="btn-primario"
              disabled={trabajando || !(parseMoney(montoAdelanto)! > 0) || faltaAdelanto !== null}
              onClick={() => void adelantar()}
            >
              <Ico icono="pagar" />
              {t('planilla.entregarAdelanto')}
            </button>
          </div>
        </Modal>
      )}

      {pagando && (
        <Modal titulo={t(pagando.esLiquidacion ? 'planilla.pagarLiquidacion' : 'planilla.pagarCorrida')} onCerrar={() => setPagandoId(null)}>
          <div className="flex flex-col gap-4" data-pagar-corrida>
            <div>
              <p className="font-medium">
                {pagando.esLiquidacion ? t('planilla.liquidacionDe', { nombre: pagando.lineas[0]?.nombre ?? '' }) : tituloDe(pagando)}
              </p>
              <div className="mt-2 flex items-baseline justify-between gap-3 text-sm">
                <span className="text-slate-400">{t('planilla.aEntregar')}</span>
                <span className="tabular text-lg font-semibold" data-a-entregar>
                  {formatMoney(subMoney(pagando.totalNeto, pagando.totalAdelantos ?? (0 as Centavos)))}
                </span>
              </div>
            </div>
            {/* Paid on another day: the money left that day. Not before the run owed it, nor before an advance it took off. */}
            <Campo
              etiqueta={t('planilla.sePagoEl')}
              type="date"
              value={fechaPago}
              min={desdeElDia}
              max={hoy()}
              onChange={(e) => setFechaPago(e.target.value)}
              error={
                fechaPagoBien
                  ? null
                  : primerDia?.nombre && fechaPago < primerDia.fecha && fechaPago >= pagando.fecha
                    ? t('planilla.antesDelAdelanto', { nombre: primerDia.nombre, fecha: primerDia.fecha })
                    : t('planilla.fechaPagoFuera', { desde: desdeElDia })
              }
            />
            <Selector
              etiqueta={t('compra.formaPago')}
              value={formaPago}
              onChange={(e) => setFormaPago(e.target.value as typeof formaPago)}
            >
              <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
              <option value="transferencia">{t('pago.transferencia')}</option>
              <option value="billetera">{t('gastos.billeteraMovil')}</option>
            </Selector>
            {formaPago === 'transferencia' && (
              <ElegirCuenta
                valor={cuentaEnCordobas(config, cuentaPago)}
                onCambiar={(c) => setCuentaPago(c as CuentaBancoCordobas)}
                etiqueta={t('bancos.pagaDe')}
              />
            )}
            {formaPago === 'efectivo_cordobas' && <p className="text-xs text-slate-500">{t('planilla.pagoDeEfectivoGeneral')}</p>}
            {error && <p className="text-sm text-rose-400">{error}</p>}
            <button type="button" className="btn-primario" disabled={trabajando || !fechaPagoBien} onClick={() => void pagar()}>
              <Ico icono="pagar" />
              {t('planilla.marcarPagada')}
            </button>
          </div>
        </Modal>
      )}
    </Pagina>
  );
}

/**
 * An aguinaldo payment in «Corridas» (2026-10-31): when and out of what, each person's, its total;
 * set right, when; voided, that it was. «Corregir» opens it in «Pagar aguinaldo»; «Colillas», each
 * person's stub of it (2026-11-01).
 */
function TarjetaAguinaldo({
  pago,
  config,
  corregidoEl,
  onCorregir,
  onColillas,
}: {
  pago: PagoAguinaldo;
  config: Settings;
  corregidoEl: string | undefined;
  onCorregir: (() => void) | undefined;
  onColillas: () => void;
}) {
  const t = useT();
  const anulado = aguinaldoAnulado(pago);
  return (
    <li className="tarjeta" data-pago-aguinaldo={pago.id}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{t('planilla.aguinaldo.titulo')}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {anulado
              ? pago.fecha
              : t('planilla.aguinaldo.resumen', { fecha: pago.fecha, n: pago.lineas.length, cuenta: deDondeSalio(config, pago.formaPago, pago.cuenta, t) })}
          </p>
          {corregidoEl && (
            <p className="mt-0.5 text-xs text-sky-300" data-corregida>
              {t('planilla.corregidaEl', { fecha: corregidoEl })}
            </p>
          )}
        </div>
        {anulado ? (
          <span className="shrink-0 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-300" data-anulada>
            {t('planilla.aguinaldo.anulado')}
          </span>
        ) : (
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="tabular font-semibold">{formatMoney(pago.total)}</span>
            <span className="text-xs text-teal-400">{t('planilla.pagadaEl', { fecha: pago.fecha })}</span>
          </div>
        )}
      </div>
      {!anulado && (
        <>
          <ul className="mt-2 flex flex-col gap-0.5 text-xs text-slate-400">
            {pago.lineas.map((l) => (
              <li key={l.empleadoId} className="flex flex-wrap justify-between gap-x-3">
                <span className="min-w-0 truncate">{l.nombre}</span>
                <span className="tabular ml-auto text-right">{formatMoney(l.monto, { symbol: false })}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-600">{t('planilla.aguinaldo.sinInss')}</p>
        </>
      )}
      {!anulado && (
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/5 pt-3">
          {onCorregir && (
            <button
              type="button"
              className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm odd:last:col-span-2"
              data-corregir-aguinaldo
              onClick={onCorregir}
            >
              <Ico icono="editar" className="size-4" />
              <span className="truncate">{t('planilla.corregir.boton')}</span>
            </button>
          )}
          <button
            type="button"
            className="btn-secundario h-11 min-h-0 min-w-0 gap-1.5 px-2 text-sm odd:last:col-span-2"
            data-colillas
            onClick={onColillas}
          >
            <Ico icono="imprimir" className="size-4" />
            <span className="truncate">{t(pago.lineas.length === 1 ? 'planilla.colillas.botonUna' : 'planilla.colillas.boton')}</span>
          </button>
        </div>
      )}
    </li>
  );
}

function Cifra({
  titulo,
  valor,
  pie,
  ayuda,
  calculo,
}: {
  titulo: string;
  valor: Centavos;
  pie?: string;
  /** What it is and how it is worked out, in a bubble on its name, with the figures it adds up. */
  ayuda: string;
  calculo?: readonly PasoDelCalculo[] | undefined;
}) {
  return (
    <div>
      <p className="text-xs text-slate-400">
        <Explicacion texto={ayuda} titulo={titulo} {...(calculo ? { calculo } : {})} className="hover:text-slate-200">
          <ConMarca>{titulo}</ConMarca>
        </Explicacion>
      </p>
      <p className="tabular mt-1 text-lg font-semibold">{formatMoney(valor)}</p>
      {pie && <p className="mt-0.5 text-xs text-slate-500">{pie}</p>}
    </div>
  );
}

function Renglon({ etiqueta, monto }: { etiqueta: string; monto: Centavos }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="truncate text-slate-300">{etiqueta}</span>
      <span className="tabular shrink-0">{formatMoney(monto)}</span>
    </div>
  );
}
